/**
 * Formats a number with commas as thousands separators and a fixed number of decimal places.
 *
 * @param number The number to format.
 * @param precision The number of decimal places to include in the formatted number.
 * @returns A string representing the formatted number.
 *
 * @deprecated Use the `NumberFormat` component instead.
 */
export const formatNumber = (number: any, precision = 0): string =>
  Number(number)
    .toFixed(precision)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')

/**
 * Formats rate per mile amount.
 *
 * @param rate The rate to format.
 * @param mile The number of miles to use in the calculation.
 * @param rawNumAmount Whether to return the raw number amount without the dollar sign and parentheses.
 * @returns A string representing the formatted rate per mile.
 */
export const formatPerMile = (rate: any, mile: any = 1, rawNumAmount = false): string => {
  if (!rate) return ''
  mile = Number(mile)
  const mileage = Math.abs(mile) > 0 ? Math.abs(mile) : 1
  const calculatedRate = Number(rate) / Number(mileage)
  const dollars = Number(calculatedRate).toFixed(2)
  const formatted = dollars.replace(/\d(?=(\d{3})+\.)/g, '$&,').replace('.00', '')
  return rawNumAmount ? formatted : `($${formatted}/mi)`
}

/**
 * Formats a currency amount with a dollar sign and commas as thousands separators.
 *
 * @param rate The currency amount to format.
 * @param whole Whether to round the amount to the nearest whole number.
 * @param floor Whether to round the amount down to the nearest whole number.
 * @param margin Whether to include a plus or minus sign for positive or negative amounts.
 * @param precision The number of decimal places to include in the formatted amount.
 * @returns A string representing the formatted currency amount.
 */
export const formatCurrency = (
  rate: any,
  whole?: boolean,
  floor?: boolean,
  margin?: boolean,
  precision = 2
): string => {
  // Only numbers or strings allowed
  if (typeof rate !== 'number' && typeof rate !== 'string') return ''
  if (Math.sign(Number(rate)) === 0 && margin) return '$0'
  // Null or impossible values resolve to empty string
  if (!rate || rate === Infinity || rate === -Infinity) return ''

  // Return strings already formatted
  if (typeof rate === 'string' && rate[0] === '$') return rate
  // Round or truncate decimals and remove trailing numbers
  if (whole) precision = 0
  if (floor) rate = Math.floor(Number(rate))
  const dollars = Number(rate).toFixed(precision).replace(/\.0+$/, '')

  // Add commas
  const formatted = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  if (Math.sign(rate) === 1 && margin) {
    const postiveMargin = `+$${formatted}`
    return postiveMargin
  }
  if (Math.sign(rate) === -1 && margin) {
    const negativeMargin = formatted.replace('-', '')
    return `-$${negativeMargin}`
  }
  // Return formatted string
  return `$${formatted}`
}

export default formatCurrency
